import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import toast from 'react-hot-toast';
import { RequestJob, PriceBase } from '../types';
import { validateUploadFile, REQUEST_ACCEPT, MAX_REQUEST_FILES } from '../utils/uploadLimits';
import { ChevronDownIcon, DocumentIcon, CloudArrowUpIcon, CheckCircleIcon, ArrowDownTrayIcon, TrashIcon } from '@heroicons/react/24/outline';

interface LeftPanelProps {
  requests: RequestJob[];
  selectedRequest: RequestJob | null;
  onRequestSelect: (req: RequestJob) => void;
  onStageSelect: (path: string) => void;
  onUploadRequest: (files: File[]) => void;
  onUploadPriceBase: (files: File[]) => void;
  onDownloadFile: (filePath: string, filename?: string) => void;
  onDeleteRequest: (reqId: number) => void;
  priceBase: PriceBase | null;
  isLoading: boolean;
}

const LeftPanelNew: React.FC<LeftPanelProps> = ({
  requests,
  selectedRequest,
  onRequestSelect,
  onStageSelect,
  onUploadRequest,
  onUploadPriceBase,
  onDownloadFile,
  onDeleteRequest,
  priceBase,
  isLoading,
}) => {
  const [isPriceUploading, setIsPriceUploading] = React.useState(false);
  const [priceStatus, setPriceStatus] = React.useState<'idle' | 'processing' | 'done' | 'error'>('idle');
  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (!acceptedFiles.length) return;
    if (acceptedFiles.length > MAX_REQUEST_FILES) {
      toast.error(`За раз можно загрузить не больше ${MAX_REQUEST_FILES} файлов.`);
      return;
    }
    // Проверяем все файлы до отправки — иначе отклонённый файл показал бы «загружено»
    for (const f of acceptedFiles) {
      const err = await validateUploadFile(f, 'request');
      if (err) { toast.error(err); return; }
    }
    onUploadRequest(acceptedFiles);
  }, [onUploadRequest]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: REQUEST_ACCEPT,
    multiple: true,
  });

  // Parse price base original filenames
  const priceFilenames: string[] = React.useMemo(() => {
    if (!priceBase?.original_filenames) return [];
    try {
      return JSON.parse(priceBase.original_filenames);
    } catch {
      return [];
    }
  }, [priceBase?.original_filenames]);

  return (
    <div className="h-full flex flex-col">
      <div className="p-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900">Заявки</h2>
      </div>

      {/* Загрузка заявки */}
      <div className="p-4 border-b border-gray-200">
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
            isDragActive
              ? 'border-blue-400 bg-blue-50'
              : 'border-gray-300 hover:border-gray-400'
          }`}
        >
          <input {...getInputProps()} />
          <CloudArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
          <p className="mt-2 text-sm text-gray-600">
            {isDragActive
              ? 'Отпустите файлы здесь...'
              : 'Перетащите файлы или нажмите для выбора'}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Excel, CSV, PDF, Word, фото — можно несколько сразу
          </p>
        </div>
      </div>

      {/* Список заявок */}
      <div className="flex-1 overflow-y-auto p-4">
        {isLoading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-sm text-gray-600">Загрузка...</p>
          </div>
        ) : requests.length === 0 ? (
          <div className="text-center py-8">
            <DocumentIcon className="mx-auto h-12 w-12 text-gray-400" />
            <p className="mt-2 text-sm text-gray-600">Нет заявок</p>
            <p className="text-xs text-gray-500">Загрузите файл заявки для начала</p>
          </div>
        ) : (
          <div className="space-y-2">
            {requests.map((req) => (
              <RequestCard
                key={req.id}
                req={req}
                isSelected={selectedRequest?.id === req.id}
                onSelect={() => onRequestSelect(req)}
                onStageSelect={onStageSelect}
                onDownloadFile={onDownloadFile}
                onDelete={() => onDeleteRequest(req.id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Информация о прайсе + кнопка загрузки (в самом низу) */}
      <div className="px-4 pb-4 pt-2 border-t border-gray-200 space-y-2">
        {/* Информация о загруженном прайсе */}
        {priceBase && priceBase.status === 'ready' && (
          <div className="text-xs text-gray-600 space-y-1 p-2 bg-gray-50 rounded">
            <div className="flex items-center justify-between">
              <span className="font-medium text-gray-700">
                Прайс {priceFilenames.length > 0 ? priceFilenames.join(', ') : ''}
              </span>
              {priceBase.normalized_path && (
                <button
                  onClick={() => onDownloadFile(priceBase.normalized_path!)}
                  className="text-blue-600 hover:text-blue-800 ml-2 flex-shrink-0"
                  title="Скачать прайс"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                </button>
              )}
            </div>
            {priceBase.created_at && (
              <p className="text-gray-500">
                загружен {new Date(priceBase.created_at + (priceBase.created_at.endsWith('Z') ? '' : 'Z')).toLocaleString('ru-RU', {
                  year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit'
                })}
              </p>
            )}
            {/* Статистика прайса */}
            {priceBase.total_positions != null && (
              <p className="text-gray-500">
                Определено {priceBase.total_positions} позиций
                {priceBase.positions_with_units != null && `, ${priceBase.positions_with_units} с ед. изм.`}
                {priceBase.positions_with_prices != null && `, ${priceBase.positions_with_prices} с ценами`}
              </p>
            )}
          </div>
        )}

        {(priceBase?.status === 'running' || isPriceUploading) && (
          <div className="flex items-center space-x-2 text-xs text-gray-600 p-2 bg-gray-50 rounded">
            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-500"></div>
            <span>Обрабатывается прайс...</span>
          </div>
        )}

        <label className={`w-full inline-flex items-center justify-center px-4 py-2 bg-blue-100 text-blue-700 rounded cursor-pointer hover:bg-blue-200 ${isPriceUploading ? 'opacity-60 cursor-not-allowed pointer-events-none' : ''}`}>
          <input type="file" className="hidden" multiple onChange={async (e) => {
            const input = e.currentTarget as HTMLInputElement;
            const files = input.files ? Array.from(input.files) : [];
            if (!files.length) return;
            // Проверка лимитов до старта обработки — иначе отклонённый файл показывал бы «готово»
            for (const f of files) {
              const verr = await validateUploadFile(f, 'price');
              if (verr) { toast.error(verr); input.value = ''; return; }
            }
            try {
              setIsPriceUploading(true);
              setPriceStatus('processing');
              const result: any = onUploadPriceBase(files);
              if (result && typeof result.then === 'function') {
                await result;
              } else {
                await new Promise((resolve) => setTimeout(resolve, 1500));
              }
              setPriceStatus('done');
            } catch (err) {
              setPriceStatus('error');
            } finally {
              setIsPriceUploading(false);
              input.value = '';
            }
          }} />
          Загрузить прайс
        </label>
        {priceStatus === 'done' && (
          <div className="flex items-center space-x-2 text-xs text-green-600 p-2">
            <CheckCircleIcon className="h-4 w-4 text-green-500" />
            <span>Обработка прайса завершена</span>
          </div>
        )}
        {priceStatus === 'error' && (
          <div className="flex items-center space-x-2 text-xs text-red-600 p-2">
            <span>Ошибка обработки прайса</span>
          </div>
        )}
      </div>
    </div>
  );
};

interface RequestCardProps {
  req: RequestJob;
  isSelected: boolean;
  onSelect: () => void;
  onStageSelect: (path: string) => void;
  onDownloadFile: (filePath: string, filename?: string) => void;
  onDelete: () => void;
}

const RequestCard: React.FC<RequestCardProps> = ({ req, isSelected, onSelect, onStageSelect, onDownloadFile, onDelete }) => {
  const [isExpanded, setIsExpanded] = React.useState(true);
  const filename = (req.uploaded_file || '').split(/[/\\]/).pop() || `Заявка #${req.id}`;

  return (
    <div
      className={`border rounded-lg p-3 cursor-pointer transition-colors ${
        isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
      }`}
      onClick={onSelect}
    >
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2">
            <h3 className="text-sm font-medium text-gray-900 truncate">{filename}</h3>
            {req.uploaded_file && (
              <button
                onClick={(e) => { e.stopPropagation(); onDownloadFile(req.uploaded_file, filename); }}
                className="text-blue-600 hover:text-blue-800 flex-shrink-0 p-0.5 rounded hover:bg-blue-50"
                title="Скачать файл"
              >
                <ArrowDownTrayIcon className="h-3.5 w-3.5" />
              </button>
            )}
            <button
              onClick={(e) => { e.stopPropagation(); if (window.confirm('Удалить заявку?')) onDelete(); }}
              className="text-gray-400 hover:text-red-600 flex-shrink-0 p-0.5 rounded hover:bg-red-50"
              title="Удалить заявку"
            >
              <TrashIcon className="h-3.5 w-3.5" />
            </button>
          </div>
          <p className="text-xs text-gray-500 truncate">
            {new Date(req.created_at ? req.created_at + (req.created_at.endsWith('Z') ? '' : 'Z') : Date.now()).toLocaleString('ru-RU', {
              year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit'
            })}
          </p>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); setIsExpanded(!isExpanded); }}
          className="p-1 hover:bg-gray-100 rounded"
        >
          <ChevronDownIcon className={`h-4 w-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {isExpanded && (
        <div className="mt-3 pt-3 border-t border-gray-200 space-y-2">
          {req.status === 'failed' && (
            <div className="p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
              {req.error || 'Не удалось обработать заявку'}
            </div>
          )}

          {(req.status === 'pending' || req.status === 'running') && (
            <div className="flex items-center space-x-2 text-xs text-gray-500 p-2">
              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-500"></div>
              <span>{req.preview_confirmed ? 'Ищем позиции в прайсе...' : 'Разбираем файл...'}</span>
            </div>
          )}

          {req.status === 'awaiting_preview' && req.preview_path && (
            <div
              className="p-2 bg-amber-50 border border-amber-300 rounded hover:bg-amber-100 cursor-pointer"
              onClick={(e) => { e.stopPropagation(); onStageSelect(req.preview_path!); }}
            >
              <div className="text-xs font-medium text-amber-900">
                Проверьте распознанное{req.preview_row_count != null ? ` (${req.preview_row_count} позиций)` : ''}
              </div>
              {req.ingest_method && (
                <div className="text-[11px] text-amber-700 mt-0.5">{req.ingest_method}</div>
              )}
            </div>
          )}
          {req.analysis_path && (
            <div className="flex items-center justify-between p-2 bg-gray-50 rounded hover:bg-gray-100 cursor-pointer"
                 onClick={(e) => { e.stopPropagation(); onStageSelect(req.analysis_path!); }}>
              <div className="flex items-center space-x-2">
                <span className="text-xs text-gray-700">
                  Результат анализа{req.analysis_row_count != null ? ` (${req.analysis_row_count} позиций)` : ''}
                </span>
              </div>
            </div>
          )}
          {req.bill_path && (
            <div className="flex items-center justify-between p-2 bg-green-50 rounded hover:bg-green-100 cursor-pointer"
                 onClick={(e) => { e.stopPropagation(); onStageSelect(req.bill_path!); }}>
              <div className="flex items-center space-x-2">
                <span className="text-xs text-green-700 font-medium">
                  Счёт{req.bill_row_count != null && req.analysis_row_count != null
                    ? ` (${req.bill_row_count} из ${req.analysis_row_count} найдено)`
                    : req.bill_row_count != null ? ` (${req.bill_row_count} позиций)` : ''}
                </span>
              </div>
            </div>
          )}
          {req.merged_path && !req.bill_path && (
            <div className="flex items-center justify-between p-2 bg-gray-50 rounded hover:bg-gray-100 cursor-pointer"
                 onClick={(e) => { e.stopPropagation(); onStageSelect(req.merged_path!); }}>
              <div className="flex items-center space-x-2">
                <span className="text-xs text-gray-700">Итоговая</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LeftPanelNew;
